import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { cn } from 'shadcn/lib/utils';

/**
 * Color input that renders a color picker alongside a hex input field.
 * Provides both a visual color picker and a text input for hex color values.
 *
 * @param {Object} props
 * @param {string} [props.defaultValue='#FFFFFF'] - Default color value in hex format
 * @param {boolean} [props.disabled] - Whether the component is disabled
 * @param {string} props.id - Unique identifier for the input element
 * @param {string} props.label - Label text for the input
 * @param {string} props.name - Name attribute for the input element
 * @param {Function} props.onChange - Callback function triggered when color value changes
 * @param {string} [props.value] - Current color value in hex format
 * @returns {JSX.Element} A color input component with visual picker and hex input
 */
function ColorInput({ defaultValue = '#FFFFFF', disabled, id, label, name, onChange, value }) {
    return (
        <div className="flex flex-col gap-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="flex gap-2">
                <div className="relative w-12 h-9 rounded-md overflow-hidden border">
                    <input
                        type="color"
                        name={name}
                        className={cn(
                            'absolute top-1/2 left-1/2 w-14 h-12 -translate-x-1/2 -translate-y-1/2 border-0 cursor-pointer',
                            disabled && 'opacity-50 cursor-not-allowed',
                        )}
                        value={value ?? defaultValue}
                        onChange={onChange}
                        disabled={disabled}
                    />
                </div>
                <Input
                    type="text"
                    name={name}
                    value={value ?? defaultValue}
                    onChange={onChange}
                    className="flex-1"
                    pattern="#[0-9A-Fa-f]{6}"
                    placeholder={defaultValue}
                    required
                    disabled={disabled}
                />
            </div>
        </div>
    );
}

export default ColorInput;
